/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Grid, Typography, styled, useTheme } from '@mui/material';
import type { FC } from 'react';
import React from 'react';
import { Trans } from 'react-i18next';
import {
  AlertBanner,
  CustomAccordion,
  CustomIconButton,
  renderCounter,
  tooltipContainerStyles,
} from '@opengeoweb/shared';
import { layerUtils } from '@opengeoweb/store';
import type { serviceTypes, mapTypes, layerTypes } from '@opengeoweb/store';
import {
  CloudLoading,
  Delete,
  Visibility,
  VisibilityOff,
} from '@opengeoweb/theme';
import { LayerInfoButton } from '@opengeoweb/webmap-react';
import { AcceptanceTime } from './AcceptanceTime/AcceptanceTime';
import ActivateLayer, { AutoOptions } from './ActivateLayer/ActivateLayer';
import DimensionSelect from './DimensionSelect/DimensionSelect';
import LayerService from './LayerService/LayerService';
import LoadDuration from './LoadDuration/LoadDuration';
import LayerManagerMenuButton from './Menubutton/MenuButton';
import OpacitySelect from './OpacitySelect/OpacitySelect';
import RenderLayers from './RenderLayers/RenderLayers';
import RenderStyles from './RenderStyles/RenderStyles';
import { useCoreTranslation } from '../../../../utils/i18n';
import { columnClasses, leftButtonsStyle } from '../../LayerManagerUtils';
import { MissingData } from './MissingData/MissingData';

const LeftButtons: FC<{
  size?: string;
  layerEnableLayout?: React.ReactElement;
  layerMissingDataLayout?: React.ReactElement;
  layerActiveLayout?: React.ReactElement;
  disableActivateLayer?: boolean;
  isEnabled?: boolean;
  dragHandle?: React.ReactElement;
  layer?: layerTypes.Layer;
  onChangeEnable: (event: React.MouseEvent<HTMLElement>) => void;
  onChangeActive: (autoOption: AutoOptions) => void;
}> = ({
  size,
  dragHandle,
  layer,
  layerMissingDataLayout,
  layerEnableLayout,
  isEnabled,
  disableActivateLayer,
  layerActiveLayout,
  onChangeEnable,
  onChangeActive,
}) => {
  return (
    <Box sx={leftButtonsStyle}>
      {dragHandle}
      {layerMissingDataLayout || (
        <MissingData layerIsInsideAcceptanceTime="equal" />
      )}
      {layerEnableLayout || (
        <CustomIconButton
          data-testid={`enableButton${size}`}
          onClick={onChangeEnable}
          shouldShowAsDisabled={!layer!.enabled}
          tooltipTitle={layer!.name!}
        >
          {isEnabled ? <Visibility /> : <VisibilityOff />}
        </CustomIconButton>
      )}
      {!disableActivateLayer &&
        (layerActiveLayout || (
          <ActivateLayer
            current={AutoOptions.BOTH}
            isEnabled={true}
            onChange={onChangeActive}
          />
        ))}
    </Box>
  );
};

const Styles: FC<{
  style: string;
  onChangeStyle: (style: string) => void;
}> = ({ style, onChangeStyle }) => (
  <RenderStyles
    currentLayerStyle={style}
    layerStyles={[]}
    onChangeLayerStyle={onChangeStyle}
  />
);

const Opacity: FC<{
  opacity: number;
  onChangeOpacity: (opacity: number) => void;
}> = ({ opacity, onChangeOpacity }) => (
  <OpacitySelect
    currentOpacity={opacity}
    onLayerChangeOpacity={onChangeOpacity}
  />
);

const DeleteLayer: FC<{
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}> = ({ onClick }) => {
  const { t } = useCoreTranslation();
  return (
    <CustomIconButton
      data-testid="deleteButton"
      onClick={onClick}
      tooltipTitle={t('layermanager-layer-delete')}
    >
      <Delete />
    </CustomIconButton>
  );
};

const ShowLayerInfo: FC<{
  onClick: () => void;
}> = ({ onClick }) => <LayerInfoButton onClick={onClick} />;

const Acceptance: FC<{
  acceptanceTime: number | null | undefined;
  onChangeAcceptanceTime: (acceptanceTime: number | null) => void;
}> = ({ acceptanceTime, onChangeAcceptanceTime }) => (
  <AcceptanceTime
    acceptanceTimeInMinutes={acceptanceTime}
    onChangeAcceptanceTime={onChangeAcceptanceTime}
  />
);

const RowItemCollapse: FC<{
  className: string;
  collapsedColumns?: Record<string, boolean>;
  children: React.ReactNode;
}> = ({ className, collapsedColumns, children }) => {
  const theme = useTheme();
  const hoverId = `${columnClasses.columnHeader}-${className}`;
  const hoverElement = document.getElementById(hoverId);

  hoverElement?.addEventListener('mouseenter', () => {
    const targetElements = document.getElementsByClassName(className);
    (Array.from(targetElements) as HTMLElement[]).forEach((element) => {
      // eslint-disable-next-line no-param-reassign
      element.style.backgroundColor =
        theme.palette.geowebColors.buttons.icon.mouseOver.fill || 'inherit';
    });
  });

  hoverElement?.addEventListener('mouseleave', () => {
    const targetElements = document.getElementsByClassName(className);
    (Array.from(targetElements) as HTMLElement[]).forEach((element) => {
      // eslint-disable-next-line no-param-reassign
      element.style.backgroundColor = 'inherit';
    });
  });

  return (
    <Grid
      className={className}
      style={{
        height: '32px',
        ...(collapsedColumns?.[className] && {
          width: '24px',
          flexGrow: 0,
          contentVisibility: 'hidden',
        }),
      }}
    >
      {children}
    </Grid>
  );
};

interface LayerRowUnstyledProps {
  layerId?: string;
  layer?: layerTypes.Layer;
  layerName?: string;
  layerServiceName?: string;
  mapId: string;
  isEDRLayer?: boolean;
  onLayerRowClick?: (layerId?: string) => void;
  onLayerEnable?: (payload: { layerId: string; enabled: boolean }) => void;
  onLayerChangeName?: (payload: { layerId: string; name: string }) => void;
  onLayerChangeStyle?: (payload: { layerId: string; style: string }) => void;
  onLayerChangeOpacity?: (payload: {
    layerId: string;
    opacity: number;
  }) => void;
  onLayerChangeDimension?: (payload: {
    origin: string;
    layerId: string;
    dimension: mapTypes.Dimension;
  }) => void;
  onLayerDelete?: (payload: { mapId: string; layerId: string }) => void;
  onLayerDuplicate?: (payload: { mapId: string; layerId: string }) => void;
  onChangeAcceptanceTime?: (payload: {
    layerId: string;
    acceptanceTime: number | null;
  }) => void;
  layerEnableLayout?: React.ReactElement;
  layerServicesLayout?: React.ReactElement;
  layerStylesLayout?: React.ReactElement;
  layerOpacityLayout?: React.ReactElement;
  layerDimensionLayout?: React.ReactElement;
  layerAcceptanceTimeLayout?: React.ReactElement;
  layerMissingDataLayout?: React.ReactElement;
  layerDeleteLayout?: React.ReactElement;
  layerShowLayerInfoLayout?: React.ReactElement;
  layerMenuLayout?: React.ReactElement;
  layerActiveLayout?: React.ReactElement;
  layerLoadDurationLayout?: React.ReactElement;
  disableActivateLayer?: boolean;
  services?: serviceTypes.Services;
  isEnabled?: boolean;
  dragHandle?: React.ReactElement;
  isLayerMissing?: boolean;
  className?: string;
  collapsedColumns?: Record<string, boolean>;
}

const LayerRowUnstyled: FC<LayerRowUnstyledProps> = ({
  layerId,
  layer,
  layerName,
  layerServiceName = '',
  services,
  mapId,
  isEDRLayer = false,
  onLayerRowClick = (): void => {},
  isEnabled = true,
  isLayerMissing = false,
  onLayerEnable,
  onLayerChangeName,
  onLayerChangeStyle,
  onLayerChangeOpacity,
  onLayerChangeDimension,
  onLayerDelete,
  onLayerDuplicate,
  onChangeAcceptanceTime,
  // layout
  layerEnableLayout,
  layerServicesLayout,
  layerStylesLayout,
  layerOpacityLayout,
  layerDimensionLayout,
  layerDeleteLayout,
  layerShowLayerInfoLayout,
  layerMenuLayout,
  layerActiveLayout,
  disableActivateLayer,
  dragHandle,
  layerAcceptanceTimeLayout,
  layerMissingDataLayout,
  layerLoadDurationLayout,
  className,
  collapsedColumns,
}: LayerRowUnstyledProps) => {
  const onClickRow = (): void => onLayerRowClick(layerId);

  const changeEnable = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    onLayerEnable!({ layerId: layerId!, enabled: !isEnabled });
  };

  const changeStyle = (style: string): void => {
    onLayerChangeStyle!({ layerId: layerId!, style });
  };

  const changeOpacity = (opacity: number): void => {
    onLayerChangeOpacity!({
      layerId: layer!.id!,
      opacity,
    });
  };

  const changeAcceptanceTime = (acceptanceTime: number | null): void => {
    onChangeAcceptanceTime?.({
      layerId: layerId!,
      acceptanceTime,
    });
  };

  const deleteLayer = (): void => onLayerDelete!({ mapId, layerId: layerId! });

  renderCounter.count(LayerRowUnstyled.name);

  return (
    // className needed for styled() utility
    <div className={className}>
      <Grid
        className="layerRow"
        container
        data-testid={`layerRow-${layerId}`}
        role="listitem"
        sx={{
          alignItems: 'center',
        }}
      >
        <Accordion
          Acceptance={
            layerAcceptanceTimeLayout || (
              <Acceptance
                acceptanceTime={layer!.acceptanceTimeInMinutes}
                onChangeAcceptanceTime={changeAcceptanceTime}
              />
            )
          }
          DeleteLayer={
            layerDeleteLayout || <DeleteLayer onClick={deleteLayer} />
          }
          LeftButtons={
            <LeftButtons
              disableActivateLayer={disableActivateLayer}
              dragHandle={dragHandle}
              isEnabled={isEnabled}
              layer={layer}
              layerActiveLayout={layerActiveLayout}
              layerEnableLayout={layerEnableLayout}
              layerMissingDataLayout={layerMissingDataLayout}
              onChangeActive={onClickRow}
              onChangeEnable={changeEnable}
              size="-medium"
            />
          }
          LoadDuration={
            layerLoadDurationLayout || (
              <LoadDuration layerId={layerId!} mapId={mapId} />
            )
          }
          Opacity={
            layerOpacityLayout || (
              <Opacity
                onChangeOpacity={changeOpacity}
                opacity={layer!.opacity!}
              />
            )
          }
          ShowLayerInfo={
            layerShowLayerInfoLayout || (
              <ShowLayerInfo onClick={(): void => {}} />
            )
          }
          Styles={
            layerStylesLayout || (
              <Styles onChangeStyle={changeStyle} style={layer!.style!} />
            )
          }
          isEDRLayer={isEDRLayer}
          isEnabled={isEnabled}
          layerName={layerName}
          layerServiceName={layerServiceName}
        />
        <Grid
          className={columnClasses.column1}
          sx={{
            alignItems: 'center',
          }}
        >
          <LeftButtons
            disableActivateLayer={disableActivateLayer}
            dragHandle={dragHandle}
            isEnabled={isEnabled}
            layer={layer}
            layerActiveLayout={layerActiveLayout}
            layerEnableLayout={layerEnableLayout}
            layerMissingDataLayout={layerMissingDataLayout}
            onChangeActive={onClickRow}
            onChangeEnable={changeEnable}
          />
        </Grid>
        <RowItemCollapse
          className={columnClasses.columnService}
          collapsedColumns={collapsedColumns}
        >
          <LayerService isEnabled={isEnabled} serviceName={layerServiceName} />
        </RowItemCollapse>
        <RowItemCollapse
          className={columnClasses.column2}
          collapsedColumns={collapsedColumns}
        >
          {layerServicesLayout || (
            <RenderLayers
              layerName={layer!.name!}
              layers={
                (services &&
                  layer!.service &&
                  services[layer!.service]?.layers) ||
                []
              }
              onChangeLayerName={(name): void => {
                onLayerChangeName!({ layerId: layer!.id!, name });
              }}
            />
          )}
        </RowItemCollapse>
        {isLayerMissing ? (
          <Grid className={columnClasses.column7}>
            <AlertBanner
              isCompact
              title={
                <Trans
                  components={{ italic: <em /> }}
                  i18nKey="layermanager-layer-error"
                  ns="core"
                  values={{ layerName, layerServiceName }}
                />
              }
            />
          </Grid>
        ) : (
          <>
            <RowItemCollapse
              className={columnClasses.column3}
              collapsedColumns={collapsedColumns}
            >
              {layerStylesLayout || (
                <Styles onChangeStyle={changeStyle} style={layer!.style!} />
              )}
            </RowItemCollapse>
            <RowItemCollapse
              className={columnClasses.column4}
              collapsedColumns={collapsedColumns}
            >
              {layerOpacityLayout || (
                <Opacity
                  onChangeOpacity={changeOpacity}
                  opacity={layer!.opacity!}
                />
              )}
            </RowItemCollapse>
            <RowItemCollapse
              className={columnClasses.column5}
              collapsedColumns={collapsedColumns}
            >
              {layerDimensionLayout || (
                <DimensionSelect
                  layerDimensions={layerUtils.filterNonTimeDimensions(
                    layer!.dimensions!,
                  )}
                  layerId={layerId!}
                  onLayerChangeDimension={(
                    dimensionName: string,
                    dimensionValue: string,
                  ): void => {
                    const dimension: mapTypes.Dimension = {
                      name: dimensionName,
                      currentValue: dimensionValue,
                    };
                    onLayerChangeDimension!({
                      origin: 'layerrow',
                      layerId: layerId!,
                      dimension,
                    });
                  }}
                />
              )}
            </RowItemCollapse>
            <RowItemCollapse
              className={columnClasses.acceptanceTime}
              collapsedColumns={collapsedColumns}
            >
              {layerAcceptanceTimeLayout || (
                <Acceptance
                  acceptanceTime={layer!.acceptanceTimeInMinutes}
                  onChangeAcceptanceTime={changeAcceptanceTime}
                />
              )}
            </RowItemCollapse>
            <Grid
              className={columnClasses.loadDuration}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              {layerLoadDurationLayout || (
                <LoadDuration layerId={layerId!} mapId={mapId} />
              )}
            </Grid>
          </>
        )}
        <Grid
          className={columnClasses.column6}
          sx={{
            alignItems: 'center',
          }}
        >
          {layerShowLayerInfoLayout || (
            <ShowLayerInfo onClick={(): void => {}} />
          )}
          {layerDeleteLayout || <DeleteLayer onClick={deleteLayer} />}
          {layerMenuLayout || (
            <LayerManagerMenuButton
              layerId={layerId}
              mapId={mapId}
              onLayerDuplicate={onLayerDuplicate}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
};

const LayerRow = styled(LayerRowUnstyled)<{ isEnabled?: boolean }>(({
  theme,
  isEnabled = true,
}) => {
  return {
    '& .MuiInputBase-root': {
      ...tooltipContainerStyles(theme, isEnabled),
    },
    '& .layerRow': {
      backgroundColor: isEnabled
        ? theme.palette.geowebColors.layerManager.tableRowDefaultCardContainer
            .fill
        : theme.palette.geowebColors.layerManager.tableRowDisabledCardContainer
            .fill,
      border: '1px solid',
      borderColor: isEnabled
        ? theme.palette.geowebColors.layerManager.tableRowDefaultCardContainer
            .borderColor
        : theme.palette.geowebColors.layerManager.tableRowDisabledCardContainer
            .borderColor,
      borderRadius: theme.shape.borderRadius,
      marginBottom: theme.spacing(0.25),
      minHeight: '32px',
    },
  };
});

export default LayerRow;

const Accordion: FC<{
  Styles: React.ReactNode;
  ShowLayerInfo: React.ReactNode;
  DeleteLayer: React.ReactNode;
  Acceptance: React.ReactNode;
  Opacity: React.ReactNode;
  layerName?: string;
  LeftButtons: React.ReactNode;
  LoadDuration: React.ReactNode;
  isEDRLayer?: boolean;
  layerServiceName: string;
  isEnabled: boolean;
}> = ({
  Styles,
  Acceptance,
  ShowLayerInfo,
  DeleteLayer,
  Opacity,
  layerName,
  LeftButtons,
  LoadDuration,
  isEDRLayer = false,
  layerServiceName,
  isEnabled,
}) => {
  const { t } = useCoreTranslation();
  const displayLayerName = isEDRLayer
    ? t('layermanager-EDR') + layerName
    : layerName;
  return (
    <CustomAccordion
      className="medium-layermanager"
      defaultExpanded={false}
      sx={{
        minHeight: 33,
        margin: 0,
        width: '100%',
        border: 'none',
        backgroundColor: 'inherit',
        '.MuiAccordionSummary-content': {
          margin: 0,
          padding: 0,
        },
      }}
      title={
        <>
          {LeftButtons}
          <Typography
            sx={{
              fontWeight: '500',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {displayLayerName}
          </Typography>
        </>
      }
    >
      <Grid container spacing={2} sx={{ padding: '16px 32px' }}>
        <Grid container size={12} sx={{ flexDirection: 'column' }}>
          <Grid>{t('layermanager-service')}</Grid>
          <Grid>
            <LayerService
              isEnabled={isEnabled}
              serviceName={layerServiceName}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid>{t('layermanager-style-title')}</Grid>
          {Styles}
        </Grid>
        <Grid
          container
          sx={{
            '.MuiButtonBase-root': {
              justifyContent: 'flex-start',
            },
          }}
        >
          <Grid>{t('layermanager-opacity-title')}</Grid>
          {Opacity}
        </Grid>
        <Grid container>
          <Grid>{t('layermanager-acceptance-time')}</Grid>
          {Acceptance}
        </Grid>
        <Grid
          container
          sx={{
            flexDirection: 'column',
          }}
        >
          <Grid>
            <CloudLoading />
          </Grid>
          {LoadDuration}
        </Grid>
        <Grid
          container
          spacing={2}
          sx={{
            justifyContent: 'center',
          }}
        >
          <Grid sx={{}}>{ShowLayerInfo}</Grid>
          <Grid sx={{}}>{DeleteLayer}</Grid>
        </Grid>
      </Grid>
    </CustomAccordion>
  );
};
